import { useEffect, useRef, useState } from 'react'
import { useAppWalkthrough } from '@/hooks/useAppWalkthrough/useAppWalkthrough'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import { UserType } from '@/hooks'
import { getWalkthroughState } from '@/utils/appWalkthroughStorage'
import { WIZARD_STORAGE_KEY } from '@/constants/walkthrough'

const shouldStartWalkthroughForUser = (
    user: { type: UserType } | null,
    customer: { customerId: number } | null,
    hasStarted: boolean,
    walkthroughState: { dismissed: boolean; completed: boolean }
): boolean => {
    if (walkthroughState.dismissed || walkthroughState.completed) {
        return false
    }

    if (hasStarted || !user) {
        return false
    }

    const hasCustomer =
        user.type === UserType.CUSTOMER ||
        (user.type === UserType.AGENT && customer !== null) ||
        (user.type === UserType.BROKER && customer !== null)

    return hasCustomer
}

export const AppWalkthrough: React.FC = () => {
    const { startWalkthrough } = useAppWalkthrough()
    const user = useAuthStore((state) => state.user)
    const customer = useAuthStore((state) => state.customer)
    const hasStartedRef = useRef(false)
    const previousCustomerIdRef = useRef<number | null>(null)
    const [spaceSelectorCompleted, setSpaceSelectorCompleted] = useState(
        () => localStorage.getItem(WIZARD_STORAGE_KEY) === 'true'
    )

    useEffect(() => {
        const handleSpaceSelectorComplete = () => {
            setSpaceSelectorCompleted(true)
        }

        window.addEventListener(
            'spaceSelectorWizardCompleted',
            handleSpaceSelectorComplete
        )

        return () => {
            window.removeEventListener(
                'spaceSelectorWizardCompleted',
                handleSpaceSelectorComplete
            )
        }
    }, [])

    useEffect(() => {
        const state = getWalkthroughState()

        if (
            !shouldStartWalkthroughForUser(
                user,
                customer,
                hasStartedRef.current,
                state
            )
        ) {
            return
        }

        if (user?.type === UserType.AGENT || user?.type === UserType.BROKER) {
            const currentCustomerId = customer?.customerId ?? null
            const previousCustomerId = previousCustomerIdRef.current

            if (!spaceSelectorCompleted) {
                return
            }

            if (
                currentCustomerId !== null &&
                (previousCustomerId === null ||
                    currentCustomerId !== previousCustomerId)
            ) {
                previousCustomerIdRef.current = currentCustomerId
                hasStartedRef.current = true

                const startStep = state.currentStep > 0 ? state.currentStep : 1
                requestAnimationFrame(() => {
                    void startWalkthrough(startStep)
                })
            } else {
                previousCustomerIdRef.current = currentCustomerId
            }
            return
        }

        // If we reach here, user must be a CUSTOMER
        hasStartedRef.current = true

        const startStep = state.currentStep > 0 ? state.currentStep : 1
        requestAnimationFrame(() => {
            void startWalkthrough(startStep)
        })
    }, [user, customer, startWalkthrough, spaceSelectorCompleted])

    useEffect(() => {
        if (!user) {
            hasStartedRef.current = false
            previousCustomerIdRef.current = null
        }
    }, [user])

    return null
}
